/**
 * Server-side FTUE persistence bridge (PEL-515).
 *
 * Wires the headless engine to account-level onboarding state. Mount once near
 * `useEventStream` in the app layout. Two responsibilities:
 *
 *  1. Hydrate — seed the engine's seen-set from the current user's
 *     `ftue_state.completedSteps` (delivered by the existing /me fetch, no extra
 *     round-trip). If the stored version differs from `FTUE_VERSION` the steps
 *     are ignored, so a version bump re-triggers the whole flow with no migration.
 *  2. Persist — register the engine's persist adapter so each mark-seen does an
 *     optimistic /me cache update (instant, survives reload) plus a
 *     fire-and-forget PATCH. The server union-merges, so concurrent tabs/devices
 *     can't clobber each other and no client-side rollback is needed on error.
 */

import { useAuth } from '@/hooks/use-auth'
import type { User } from '@/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { ftueService } from './ftue.service'
import { setFtuePersist, useFtueStore } from './ftue.store'
import { FTUE_VERSION } from './types'

/** Optimistically union new step ids into the cached user's ftue_state. */
export function mergeUserFtue(user: User, ids: string[]): User {
  const prev = user.ftue_state
  const base = prev && prev.version === FTUE_VERSION ? prev.completedSteps : []
  const completedSteps = Array.from(new Set([...base, ...ids])).sort()
  return {
    ...user,
    ftue_state: {
      version: FTUE_VERSION,
      completedSteps,
      completedAt: prev?.completedAt ?? null,
      loginCount: prev?.loginCount ?? 0,
    },
  }
}

export function useFtueServerSync(): void {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const mutation = useMutation({
    mutationFn: (completedSteps: string[]) => ftueService.updateFtueState(completedSteps),
    retry: 3,
    onSuccess: (updated) =>
      queryClient.setQueryData<User | undefined>(['user'], (current) => {
        const locallyCompleted = current?.ftue_state?.completedSteps ?? []
        return mergeUserFtue(updated, locallyCompleted)
      }),
  })
  const mutateRef = useRef(mutation.mutate)
  const hydratedUserIdRef = useRef<number | string | null>(null)
  mutateRef.current = mutation.mutate

  // Hydrate the engine when server state loads (and on any /me change).
  useEffect(() => {
    if (!user) {
      if (hydratedUserIdRef.current != null) {
        hydratedUserIdRef.current = null
        useFtueStore.getState().hydrate([], true)
      }
      return
    }
    const state = user.ftue_state
    const steps = state && state.version === FTUE_VERSION ? state.completedSteps : []
    const userChanged = hydratedUserIdRef.current !== user.id
    hydratedUserIdRef.current = user.id
    // A null server state is an intentional admin reset. Treat it as authoritative
    // even when this account is already open, instead of unioning old in-memory steps.
    useFtueStore.getState().hydrate(steps, userChanged || state == null)
  }, [user])

  // Register the persist adapter (optimistic cache update + fire-and-forget PATCH).
  useEffect(() => {
    setFtuePersist({
      markSeen: (ids) => {
        queryClient.setQueryData<User | undefined>(['user'], (prev) =>
          prev ? mergeUserFtue(prev, ids) : prev,
        )
        // Send the full in-memory union. If an earlier request exhausted its
        // retries, the next successful completion repairs that missing write
        // instead of sending only the newest id.
        mutateRef.current(Array.from(useFtueStore.getState().seen).sort())
      },
    })
    return () => setFtuePersist(null)
  }, [queryClient])
}
